// src/pages/PainelCliente/MeusPontos.js
// Extrato de pontos. Os filtros agora valem na hora (sem botão "Filtrar"),
// e a data final passa a incluir o próprio dia.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { Clock, Download, FileEdit, Gift } from "lucide-react";
import axios from "../../axiosConfig";
import { formatarPontos, lerUsuario } from "../../utils/tikpontos";
import "./MeusPontos.css";

const POR_PAGINA = 15;

const STATUS = {
  aprovado: { nome: "Aprovado", tom: "verde" },
  reservado: { nome: "Reservado", tom: "laranja" },
  resgate: { nome: "Resgate", tom: "roxo" },
  rejeitado: { nome: "Rejeitado", tom: "vermelho" },
  "em análise": { nome: "Em análise", tom: "" },
  estorno: { nome: "Estorno", tom: "azul" },
  conquista: { nome: "Conquista", tom: "verde" },
  campanha: { nome: "Campanha", tom: "verde" },
};

const FILTROS_VAZIOS = { status: "", dataInicio: "", dataFim: "", numeroPedido: "" };

const formatarData = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");

function MeusPontos() {
  const usuario = useMemo(() => lerUsuario(), []);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [expiracoes, setExpiracoes] = useState([]);
  const [saldo, setSaldo] = useState({ pontos_totais: 0, pontos_reservados: 0, pontos_disponiveis: 0 });
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    if (!usuario?.id) return;
    const id = usuario.id;

    axios
      .get(`/usuarios/${id}/historico-pontos`)
      .then((res) => setHistorico(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Erro ao buscar histórico de pontos:", err))
      .finally(() => setCarregando(false));

    axios
      .get(`/clientes/${id}/pontos`)
      .then((res) => setSaldo(res.data || {}))
      .catch((err) => console.error("Erro ao buscar resumo de pontos:", err));

    axios
      .get(`/clientes/${id}/avisos-expiracao`)
      .then((res) => setExpiracoes(res.data?.expiraBreve ? res.data.detalhes || [] : []))
      .catch((err) => console.error("Erro ao buscar expiração:", err));
  }, [usuario?.id]);

  const filtrado = useMemo(() => {
    let lista = [...historico];
    if (filtros.status) lista = lista.filter((p) => p.status === filtros.status);
    if (filtros.dataInicio) {
      const di = new Date(`${filtros.dataInicio}T00:00:00`);
      lista = lista.filter((p) => p.data_credito && new Date(p.data_credito) >= di);
    }
    if (filtros.dataFim) {
      const df = new Date(`${filtros.dataFim}T23:59:59`);
      lista = lista.filter((p) => p.data_credito && new Date(p.data_credito) <= df);
    }
    const pedido = filtros.numeroPedido.trim();
    if (pedido) {
      lista = lista.filter((p) => `${p.motivo || ""} ${p.origem || ""}`.includes(pedido));
    }
    return lista.sort((a, b) => new Date(b.data_credito) - new Date(a.data_credito));
  }, [historico, filtros]);

  useEffect(() => setPagina(1), [filtros]);

  const totalPaginas = Math.max(1, Math.ceil(filtrado.length / POR_PAGINA));
  const paginaLista = filtrado.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const temFiltro = Object.values(filtros).some(Boolean);

  const total = Number(saldo.pontos_totais || 0);
  const reservados = Number(saldo.pontos_reservados || 0);
  const disponiveis = Number(saldo.pontos_disponiveis || 0);
  const pct = (v) => (total > 0 ? `${Math.round((v / total) * 100)}%` : "0%");

  const exportarExcel = () => {
    const dados = filtrado.map((p) => ({
      Pontos: Math.ceil(p.pontos),
      Motivo: p.motivo,
      Origem: p.origem,
      "Data de crédito": p.data_credito ? new Date(p.data_credito).toLocaleDateString("pt-BR") : "",
      "Expira em": p.data_expiracao ? new Date(p.data_expiracao).toLocaleDateString("pt-BR") : "",
      Status: STATUS[p.status]?.nome || p.status,
    }));
    const planilha = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, planilha, "Extrato de Pontos");
    XLSX.writeFile(wb, "extrato-pontos-tikpag.xlsx");
  };

  const mudar = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: e.target.value }));

  return (
    <div className="mp">
      <header className="tk-cabecalho-pagina">
        <div>
          <h1 className="tk-titulo-pagina">Meus pontos</h1>
          <p className="tk-subtitulo-pagina">Seu saldo e o extrato de tudo o que entrou e saiu.</p>
        </div>
        <div className="mp-acoes">
          <Link to="/painel-cliente/catalogo-premios" className="tk-btn tk-btn--primario">
            <Gift size={17} aria-hidden="true" /> Trocar pontos
          </Link>
          <Link to="/painel-cliente/cadastrar-operacao" className="tk-btn tk-btn--secundario">
            <FileEdit size={17} aria-hidden="true" /> Cadastrar operação
          </Link>
          <button type="button" className="tk-btn tk-btn--secundario" onClick={exportarExcel} data-tour="mp-exportar">
            <Download size={17} aria-hidden="true" /> Exportar Excel
          </button>
        </div>
      </header>

      <section className="mp-resumo" data-tour="mp-saldo">
        <div className="mp-resumo-card is-disponivel">
          <span>Disponíveis para trocar</span>
          <strong>{formatarPontos(Math.ceil(disponiveis))}</strong>
          <small>{pct(disponiveis)} do total</small>
        </div>
        <div className="mp-resumo-card">
          <span>Reservados</span>
          <strong>{formatarPontos(Math.ceil(reservados))}</strong>
          <small>Em resgates ainda não concluídos</small>
        </div>
        <div className="mp-resumo-card">
          <span>Total acumulado</span>
          <strong>{formatarPontos(Math.ceil(total))}</strong>
          <small>Disponíveis + reservados</small>
        </div>
      </section>

      {expiracoes.length > 0 && (
        <section className="mp-expira" data-tour="mp-expiracao">
          <div className="mp-expira-topo">
            <Clock size={20} aria-hidden="true" />
            <strong>Você tem pontos que vencem em breve</strong>
            <Link to="/painel-cliente/catalogo-premios">Usar agora</Link>
          </div>
          <ul>
            {expiracoes.map((e, i) => (
              <li key={i}>
                <strong>{formatarPontos(e.pontos)} pts</strong> vencem em {e.data}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mp-filtros" data-tour="mp-filtros" aria-label="Filtrar extrato">
        <label className="tk-campo">
          Data inicial
          <input className="tk-input" type="date" value={filtros.dataInicio} onChange={mudar("dataInicio")} />
        </label>
        <label className="tk-campo">
          Data final
          <input className="tk-input" type="date" value={filtros.dataFim} onChange={mudar("dataFim")} />
        </label>
        <label className="tk-campo">
          Status
          <select className="tk-select" value={filtros.status} onChange={mudar("status")}>
            <option value="">Todos</option>
            {Object.entries(STATUS).map(([valor, { nome }]) => (
              <option key={valor} value={valor}>
                {nome}
              </option>
            ))}
          </select>
        </label>
        <label className="tk-campo">
          Nº do pedido
          <input
            className="tk-input"
            type="text"
            inputMode="numeric"
            placeholder="Ex.: 123456"
            value={filtros.numeroPedido}
            onChange={mudar("numeroPedido")}
          />
        </label>
        {temFiltro && (
          <button type="button" className="tk-btn tk-btn--fantasma mp-limpar" onClick={() => setFiltros(FILTROS_VAZIOS)}>
            Limpar filtros
          </button>
        )}
      </section>

      <section className="mp-historico" data-tour="mp-historico">
        <div className="mp-historico-topo">
          <h2 className="tk-secao-titulo">Extrato</h2>
          <span>
            {filtrado.length} {filtrado.length === 1 ? "lançamento" : "lançamentos"}
          </span>
        </div>

        {carregando ? (
          <div className="tk-skeleton mp-skeleton" />
        ) : paginaLista.length === 0 ? (
          <div className="tk-vazio">
            <strong>{temFiltro ? "Nenhum lançamento com esses filtros" : "Você ainda não tem lançamentos"}</strong>
            <span>
              {temFiltro
                ? "Mude as datas ou o status para ver outros lançamentos."
                : "Cadastre uma operação para começar a acumular pontos."}
            </span>
          </div>
        ) : (
          <div className="mp-tabela-caixa">
            <table className="mp-tabela">
              <thead>
                <tr>
                  <th scope="col">Pontos</th>
                  <th scope="col">Motivo</th>
                  <th scope="col">Origem</th>
                  <th scope="col">Data</th>
                  <th scope="col">Vence em</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginaLista.map((p, i) => {
                  const st = STATUS[p.status] || { nome: p.status, tom: "" };
                  const negativo = Number(p.pontos) < 0;
                  return (
                    <tr key={`${p.id || i}-${p.data_credito}`}>
                      <td data-rotulo="Pontos" className={`mp-pontos ${negativo ? "is-negativo" : ""}`}>
                        {negativo ? "" : "+"}
                        {formatarPontos(Math.ceil(p.pontos))} pts
                      </td>
                      <td data-rotulo="Motivo">{p.motivo || "—"}</td>
                      <td data-rotulo="Origem">{p.origem || "—"}</td>
                      <td data-rotulo="Data">{formatarData(p.data_credito)}</td>
                      <td data-rotulo="Vence em">{formatarData(p.data_expiracao)}</td>
                      <td data-rotulo="Status">
                        <span className={`tk-etiqueta ${st.tom ? `tk-etiqueta--${st.tom}` : ""}`}>{st.nome}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPaginas > 1 && (
          <nav className="tk-paginacao" aria-label="Páginas do extrato">
            <button
              type="button"
              className="tk-btn tk-btn--secundario"
              onClick={() => setPagina((p) => Math.max(1, p - 1))}
              disabled={pagina === 1}
            >
              Anterior
            </button>
            <span>
              Página {pagina} de {totalPaginas}
            </span>
            <button
              type="button"
              className="tk-btn tk-btn--secundario"
              onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
              disabled={pagina === totalPaginas}
            >
              Próxima
            </button>
          </nav>
        )}
      </section>
    </div>
  );
}

export default MeusPontos;
